// ProtectedRoute.jsx - Auto-logout on unauthorized access
import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../AuthContext';

const LoadingSpinner = ({ message = "Verifying access..." }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#666',
    flexDirection: 'column',
    backgroundColor: '#f8fafc'
  }}>
    <div style={{ 
      width: '50px', 
      height: '50px', 
      border: '4px solid #e2e8f0',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '24px'
    }}></div>
    <div style={{ textAlign: 'center', maxWidth: '300px' }}>
      <div style={{ marginBottom: '12px', fontWeight: '600', color: '#374151' }}>
        🔍 {message}
      </div>
      <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
        Please wait while we verify your permissions.
      </div>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// ✅ Normalize roles to handle case variations
const normalizeRole = (role) => {
  if (!role) return '';
  
  const roleStr = String(role).toUpperCase().trim();
  
  const roleMap = {
  'SUPERADMIN': 'Superadmin',
  'SUPER_ADMIN': 'Superadmin',
  'SUPER ADMIN': 'Superadmin',
  'SUPERADMINISTRATOR': 'Superadmin',
  'ADMIN': 'Admin',        // ✅ was mapping to 'CEO' — fixed
  'ADMINISTRATOR': 'Admin', // ✅ same
  'CEO': 'Admin',           // ✅ if 'CEO' ever appears, map it too
  'HR': 'HR',
  'HUMAN RESOURCES': 'HR',
  'HUMAN_RESOURCES': 'HR',
  'MANAGER': 'Manager',
  'EMPLOYEE': 'Employee',
  'CONSULTANT': 'Consultant',
  'network_manager': 'Network Manager',
  'NETWORK_MANAGER': 'Network Manager',
  'NETWORK MANAGER': 'Network Manager',
  'NETWORKMANAGER': 'Network Manager',
};
  
  return roleMap[roleStr] || roleStr;
};

// ✅ Case-insensitive role matching
const rolesMatch = (userRole, allowedRoles) => {
  if (!userRole || !allowedRoles || allowedRoles.length === 0) {
    return false;
  }
  
  const normalizedUserRole = normalizeRole(userRole);
  const normalizedAllowedRoles = allowedRoles.map(role => normalizeRole(role));
  
  console.log('🔍 Role matching:', {
    userRole,
    normalizedUserRole,
    allowedRoles,
    normalizedAllowedRoles,
    matches: normalizedAllowedRoles.includes(normalizedUserRole)
  });
  
  return normalizedAllowedRoles.includes(normalizedUserRole);
};

const ProtectedRoute = ({ children, allowedRoles = [], fallbackRole = null }) => {
  const { 
    isAuthenticated, 
    userRole, 
    isLoading, 
    initialized,
    getRoleBasedRedirect,
    refreshAuth,
    refreshUserRole,
    handleLogout,
    error,
    lastRoleCheck
  } = useAuth();
  
  const location = useLocation();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [roleWaitTime, setRoleWaitTime] = useState(0);
  const [hasLoggedAccess, setHasLoggedAccess] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // 🔥 CRITICAL: Auto-logout on unauthorized access
  const performAutoLogout = useCallback(async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    
    console.log('🚪 Unauthorized access detected - auto-logging out...');
    setIsLoggingOut(true);
    
    try {
      // Set logout flag immediately
      sessionStorage.setItem('logout_in_progress', 'true');
      
      // Clear all storage
      sessionStorage.clear();
      localStorage.clear();
      
      // Clear cookies
      document.cookie.split(";").forEach(cookie => {
        const name = cookie.trim().split("=")[0];
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        const domain = window.location.hostname.split('.').slice(-2).join('.');
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain}`;
      });
      
      // Call backend logout
      await handleLogout();
      
      // Force redirect to login
      window.location.href = '/?logout=true&reason=unauthorized';
      
    } catch (error) {
      console.error('❌ Auto-logout error:', error);
      // Force redirect even on error
      window.location.href = '/?logout=true&reason=unauthorized';
    }
  }, [handleLogout, isLoggingOut]);

  // Log state changes when meaningful values change
  useEffect(() => {
    if (initialized && !isLoading) {
      console.log('🔐 ProtectedRoute state:', {
        isAuthenticated,
        userRole,
        userRoleNormalized: normalizeRole(userRole),
        isLoading,
        initialized,
        path: location.pathname,
        allowedRoles,
        allowedRolesNormalized: allowedRoles.map(r => normalizeRole(r)),
        lastRoleCheck: lastRoleCheck ? new Date(lastRoleCheck).toISOString() : null
      });
    }
  }, [isAuthenticated, userRole, initialized, isLoading, location.pathname]);

  // Handle role waiting with timeout
  useEffect(() => {
    if (isAuthenticated && !userRole && !isLoading) {
      const timer = setInterval(() => {
        setRoleWaitTime(prev => {
          const newTime = prev + 100;
          
          if (newTime === 3000) {
            console.log('⏰ Role wait timeout, attempting to refresh role...');
            refreshUserRole();
          }
          
          if (newTime >= 5000) {
            console.log('⏰ Extended role wait, incrementing retry count');
            setRetryCount(prev => Math.min(prev + 1, 3));
            return 0;
          }
          
          return newTime;
        });
      }, 100);
      
      return () => clearInterval(timer);
    } else {
      setRoleWaitTime(0);
    }
  }, [isAuthenticated, userRole, isLoading, refreshUserRole]);

  // Handle retry logic
  const handleRetry = useCallback(async () => {
    if (retryCount >= 3) return;
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    setRoleWaitTime(0);
    
    try {
      console.log(`🔄 Retrying authentication (attempt ${retryCount + 1}/3)...`);
      await refreshAuth();
    } catch (error) {
      console.error('❌ Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  }, [retryCount, refreshAuth]);

  // Reset retry count when location or authentication state changes
  useEffect(() => {
    setRetryCount(0);
    setRoleWaitTime(0);
    setHasLoggedAccess(false);
  }, [location.pathname, isAuthenticated]);

  // Show loading spinner during logout
  if (isLoggingOut) {
    return <LoadingSpinner message="Logging out due to unauthorized access..." />;
  }

  // Wait for initialization AND loading to complete
  if (!initialized || isLoading || isRetrying) {
    const message = isRetrying 
      ? `Retrying authentication... (${retryCount}/3)`
      : "Verifying access...";
    
    console.log(`⏳ Showing loading: ${message}`);
    return <LoadingSpinner message={message} />;
  }

  // Check authentication AFTER loading completes
  if (!isAuthenticated) {
    console.log('🚫 Not authenticated, redirecting to login');
    return <Navigate 
      to="/" 
      replace 
      state={{ from: location, error: 'Authentication required' }} 
    />;
  }

  // Check for role AFTER authentication is confirmed
  if (!userRole) {
    console.warn('⚠️ User authenticated but role is null/undefined');
    
    if (roleWaitTime < 3000) {
      return <LoadingSpinner message={`Loading user permissions... (${(roleWaitTime/1000).toFixed(1)}s)`} />;
    }
    
    // 🔥 CRITICAL: Auto-logout if role is null after waiting
    console.log('❌ Role is null after wait, triggering auto-logout');
    performAutoLogout();
    return <LoadingSpinner message="Logging out due to permission error..." />;
  }

  // 🔥 CRITICAL: Auto-logout on unauthorized access
  if (allowedRoles.length > 0 && !rolesMatch(userRole, allowedRoles)) {
    console.log(`🚫 Access denied: User role '${userRole}' (normalized: '${normalizeRole(userRole)}') not in allowed roles:`, allowedRoles);
    
    // Check if this is a fallback role scenario
    if (fallbackRole && normalizeRole(userRole) === normalizeRole(fallbackRole)) {
      console.log(`✅ Access granted via fallback role: ${fallbackRole}`);
      return children;
    }

    // 🔥 CRITICAL: Immediately trigger auto-logout
    console.log('🔥 Unauthorized access - triggering immediate logout');
    performAutoLogout();
    return <LoadingSpinner message="Unauthorized access - logging out..." />;
  }

  // Only log access granted ONCE per route
  if (!hasLoggedAccess) {
    console.log(`✅ Access granted: User role '${userRole}' is allowed for path '${location.pathname}'`);
    setHasLoggedAccess(true);
  }

  return children;
};

// Higher-order component version for convenience
export const withProtectedRoute = (Component, allowedRoles = []) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute allowedRoles={allowedRoles}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// Hook for conditional rendering based on roles
export const useRoleAccess = (allowedRoles = []) => {
  const { userRole, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading || !isAuthenticated) return { hasAccess: false, isLoading: true };
  if (allowedRoles.length === 0) return { hasAccess: true, isLoading: false };
  
  return {
    hasAccess: rolesMatch(userRole, allowedRoles),
    isLoading: false,
    userRole
  };
};

export default ProtectedRoute;